import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Append-only, time-stamped notes log used for both item-level Decoration /
 * Production Notes and order-level Notes. Each entry shows `at · by · text`.
 *
 * Props:
 *   entries          existing list of {at, by, text, role}
 *   addUrl           POST endpoint that accepts {"text": "..."} and returns {entry, log_count}
 *   deleteUrlPrefix  DELETE endpoint prefix (the index gets appended) — null disables delete
 *   onChange         callback({entries, latestText}) after every successful add/delete
 *   placeholder      input placeholder
 *   title            section header
 *   accentColor      header / dot color (hex)
 *   testIdPrefix     unique testid prefix to disambiguate multiple instances on one page
 */
const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const TimestampedNotes = ({
  entries = [],
  addUrl,
  deleteUrlPrefix = null,
  onChange = null,
  placeholder = 'Add a note (e.g. white thread, 10K stitches, due Friday)…',
  title = 'Production Notes',
  accentColor = '#92400e',
  testIdPrefix = 'production-notes',
  compact = false,
}) => {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      const r = await axios.post(addUrl, { text });
      const newEntry = r.data?.entry;
      const next = [...entries, newEntry];
      setDraft('');
      if (onChange) onChange({ entries: next, latestText: text });
      toast.success('Note added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add note');
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async (idx) => {
    if (!deleteUrlPrefix) return;
    if (!window.confirm('Remove this note from the log? (admin only)')) return;
    try {
      await axios.delete(`${deleteUrlPrefix}/${idx}`);
      const next = entries.filter((_, i) => i !== idx);
      if (onChange) onChange({
        entries: next,
        latestText: next.length ? next[next.length - 1].text : '',
      });
      toast.success('Note removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove note');
    }
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl-Enter submits without needing to click the button
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      addNote();
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-block`}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8, gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: accentColor,
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          {title} {entries.length > 0 && `(${entries.length})`}
        </span>
      </div>

      {/* Existing entries */}
      {entries.length > 0 && (
        <ol style={{
          listStyle: 'none', padding: 0, margin: '0 0 8px 0',
          borderLeft: `2px solid ${accentColor}33`,
        }}>
          {entries.map((ev, i) => (
            <li key={i}
              data-testid={`${testIdPrefix}-entry-${i}`}
              style={{ position: 'relative', paddingLeft: 14, paddingBottom: 8 }}
            >
              <span style={{
                position: 'absolute', left: -5, top: 6,
                width: 8, height: 8, borderRadius: '50%',
                background: accentColor, border: '2px solid white',
                boxShadow: `0 0 0 1px ${accentColor}`,
              }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, color: '#475569', fontWeight: 600 }}>
                  {formatTimestamp(ev.at)}
                </span>
                {ev.by && (
                  <span style={{ fontSize: 10, color: '#64748b' }}>
                    by <strong>{ev.by}</strong>
                  </span>
                )}
                {deleteUrlPrefix && (
                  <button
                    type="button"
                    onClick={() => removeNote(i)}
                    title="Remove (admin)"
                    data-testid={`${testIdPrefix}-delete-${i}`}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#94a3b8', padding: 0, marginLeft: 'auto',
                    }}
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
              <div style={{
                marginTop: 2, fontSize: compact ? 12 : 13, lineHeight: 1.4,
                color: '#0f172a', whiteSpace: 'pre-wrap',
              }}>
                {ev.text}
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* New entry input */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-input`}
          rows={compact ? 2 : 2}
          style={{
            flex: 1, padding: 8, fontSize: 12, lineHeight: 1.45,
            background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 6,
            fontFamily: 'inherit', resize: 'vertical', minHeight: 44,
          }}
        />
        <Button
          onClick={addNote}
          disabled={saving || !draft.trim()}
          data-testid={`${testIdPrefix}-add-btn`}
          size="sm"
          style={{
            background: accentColor, color: 'white', height: 44,
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add
        </Button>
      </div>
      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
        Tip: ⌘/Ctrl + Enter to add quickly. All entries are time-stamped and kept forever.
      </div>
    </div>
  );
};

export default TimestampedNotes;
